/**
 * The signed-in company account.
 *
 * One account owns one company. Tokens live in local storage so a refresh does
 * not sign the company out mid-posting, and the id token — the one the API
 * gateway's authorizer accepts — is refreshed a minute before it lapses rather
 * than after a request has already failed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useConfig } from './config';
import {
  AuthError,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  refreshSession,
  resendConfirmationCode,
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  type CognitoConfig,
  type SessionTokens,
} from './cognito';

const STORAGE_KEY = 'bidtohire.session';
/** Refresh this far ahead of expiry, so a request never carries a stale token. */
const REFRESH_MARGIN_MS = 60_000;

function readStoredSession(): SessionTokens | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Partial<SessionTokens>;
    if (
      typeof parsed.idToken === 'string' &&
      typeof parsed.accessToken === 'string' &&
      typeof parsed.refreshToken === 'string' &&
      typeof parsed.expiresAt === 'number'
    ) {
      return parsed as SessionTokens;
    }
  } catch {
    // A browser that refuses storage simply starts signed out.
  }
  return undefined;
}

function writeStoredSession(session: SessionTokens | undefined): void {
  try {
    if (session === undefined) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage is a convenience; the session still works for this page view.
  }
}

export interface AuthState {
  readonly email: string | undefined;
  readonly isSignedIn: boolean;
  /** True until the configuration needed to sign in has arrived. */
  readonly isReady: boolean;
  signUp(email: string, password: string): Promise<{ readonly confirmationRequired: boolean }>;
  confirm(email: string, code: string): Promise<void>;
  resendCode(email: string): Promise<void>;
  /** Email a reset code. Succeeds whether or not the address has an account. */
  requestPasswordReset(email: string): Promise<void>;
  resetPassword(email: string, code: string, newPassword: string): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signOut(): void;
  /** A valid id token, refreshed if needed; undefined when signed out. */
  token(): Promise<string | undefined>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const config = useConfig();
  const [session, setSession] = useState<SessionTokens | undefined>(readStoredSession);
  const inFlight = useRef<Promise<SessionTokens | undefined> | undefined>(undefined);

  const cognito: CognitoConfig | undefined = config?.cognito;

  const store = useCallback((next: SessionTokens | undefined) => {
    writeStoredSession(next);
    setSession(next);
  }, []);

  const requireConfig = useCallback((): CognitoConfig => {
    if (cognito === undefined) {
      throw new AuthError(
        'CONFIG_UNAVAILABLE',
        'Accounts are temporarily unavailable. Try again shortly.',
      );
    }
    return cognito;
  }, [cognito]);

  const value = useMemo<AuthState>(() => {
    return {
      email: session?.email,
      isSignedIn: session !== undefined,
      isReady: cognito !== undefined,

      async signUp(email, password) {
        return cognitoSignUp(requireConfig(), email.trim().toLowerCase(), password);
      },

      async confirm(email, code) {
        await confirmSignUp(requireConfig(), email.trim().toLowerCase(), code.trim());
      },

      async resendCode(email) {
        await resendConfirmationCode(requireConfig(), email.trim().toLowerCase());
      },

      async requestPasswordReset(email) {
        await forgotPassword(requireConfig(), email.trim().toLowerCase());
      },

      async resetPassword(email, code, newPassword) {
        await confirmForgotPassword(
          requireConfig(),
          email.trim().toLowerCase(),
          code.trim(),
          newPassword,
        );
      },

      async signIn(email, password) {
        const tokens = await cognitoSignIn(requireConfig(), email.trim().toLowerCase(), password);
        store(tokens);
      },

      signOut() {
        store(undefined);
      },

      async token() {
        if (session === undefined) return undefined;
        if (session.expiresAt - REFRESH_MARGIN_MS > Date.now()) return session.idToken;
        if (cognito === undefined) return undefined;

        // Concurrent requests share one refresh rather than racing three.
        inFlight.current ??= refreshSession(cognito, session)
          .then((next) => {
            store(next);
            return next;
          })
          .catch(() => {
            store(undefined);
            return undefined;
          })
          .finally(() => {
            inFlight.current = undefined;
          });

        const refreshed = await inFlight.current;
        return refreshed?.idToken;
      },
    };
  }, [cognito, requireConfig, session, store]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (state === undefined) throw new Error('useAuth must be used inside AuthProvider');
  return state;
}
